import { driver, type DriveStep } from "driver.js";
import type { NavigateFunction } from "react-router-dom";
import { dashboardPaths } from "@apps/dashboard/lib/routes";

type Translate = (key: string) => string;

let lastTourStartMs = 0;
const DEDUPE_MS = 600;
const NAV_DELAY_MS = 520;
const NAV_DELAY_BUDGET_MS = 780;

function currentPathname(): string {
  const p = window.location.pathname;
  return p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
}

function pathMatches(required: string | null): boolean {
  if (required == null) return true;
  const c = currentPathname();
  if (required === "/") return c === "/" || c === "";
  return c === required;
}

/** Dos barras (desktop + drawer) con el mismo `data-tour`; prioriza la visible. */
function firstVisibleMatch(selector: string): Element | null {
  const nodes = document.querySelectorAll(selector);
  for (let i = 0; i < nodes.length; i++) {
    const el = nodes[i];
    if (!(el instanceof HTMLElement)) continue;
    const st = getComputedStyle(el);
    if (st.visibility === "hidden" || st.display === "none") continue;
    const r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) continue;
    return el;
  }
  return null;
}

export interface DashboardProductTourOptions {
  /** Hogar para rutas /members y /budget; primer hogar o activo. */
  tourHouseId: string | null;
  hasAnyHouse: boolean;
}

interface StepBlueprint {
  path: string | null;
  selector: string;
  titleKey: string;
  descKey: string;
}

function buildBlueprints(opts: DashboardProductTourOptions): StepBlueprint[] {
  const hid = opts.tourHouseId;
  const intro: StepBlueprint[] = [
    {
      path: "/",
      selector: '[data-tour="sidebar-home"]',
      titleKey: "dashboardUi.productTour.sidebarHomeTitle",
      descKey: "dashboardUi.productTour.sidebarHomeDesc",
    },
    {
      path: dashboardPaths.houses,
      selector: '[data-tour="houses-page-create"]',
      titleKey: "dashboardUi.productTour.housesCreateTourTitle",
      descKey: "dashboardUi.productTour.housesCreateTourDesc",
    },
  ];

  if (!opts.hasAnyHouse || !hid) {
    intro.push({
      path: dashboardPaths.houses,
      selector: '[data-tour="houses-empty-tour"]',
      titleKey: "dashboardUi.productTour.housesEmptyTourTitle",
      descKey: "dashboardUi.productTour.housesEmptyTourDesc",
    });
    return intro;
  }

  return [
    ...intro,
    {
      path: dashboardPaths.houses,
      selector: '[data-tour="houses-card-members"]',
      titleKey: "dashboardUi.productTour.housesMembersLinkTourTitle",
      descKey: "dashboardUi.productTour.housesMembersLinkTourDesc",
    },
    {
      path: dashboardPaths.houseMembers(hid),
      selector: '[data-tour="members-invite-section"]',
      titleKey: "dashboardUi.productTour.membersInviteTourTitle",
      descKey: "dashboardUi.productTour.membersInviteTourDesc",
    },
    {
      path: dashboardPaths.houseMembers(hid),
      selector: '[data-tour="members-split-section"]',
      titleKey: "dashboardUi.productTour.membersSplitTourTitle",
      descKey: "dashboardUi.productTour.membersSplitTourDesc",
    },
    {
      path: dashboardPaths.houseBudget(hid),
      selector: '[data-tour="budget-hero-section"]',
      titleKey: "dashboardUi.productTour.budgetHeroTourTitle",
      descKey: "dashboardUi.productTour.budgetHeroTourDesc",
    },
    {
      path: dashboardPaths.houseBudget(hid),
      selector: '[data-tour="budget-goal-section"]',
      titleKey: "dashboardUi.productTour.budgetGoalTourTitle",
      descKey: "dashboardUi.productTour.budgetGoalTourDesc",
    },
    {
      path: "/",
      selector: '[data-tour="sidebar-add-expense"]',
      titleKey: "dashboardUi.productTour.sidebarAddExpenseTitle",
      descKey: "dashboardUi.productTour.sidebarAddExpenseClosingDesc",
    },
  ];
}

function delayForPath(target: string | null): number {
  if (target?.includes("/budget")) return NAV_DELAY_BUDGET_MS;
  if (target?.includes("/members")) return NAV_DELAY_MS + 120;
  return NAV_DELAY_MS;
}

/**
 * Tour guiado con navegación SPA: Casas (alta) → enlace miembros → invitaciones/reparto → presupuesto → vuelta a nuevo gasto.
 */
export function startDashboardProductTour(
  t: Translate,
  navigate: NavigateFunction,
  opts: DashboardProductTourOptions,
): void {
  const now = Date.now();
  if (now - lastTourStartMs < DEDUPE_MS) {
    return;
  }
  lastTourStartMs = now;

  if (document.body.classList.contains("driver-active")) {
    return;
  }

  const blueprints = buildBlueprints(opts);
  const pathsForStep: (string | null)[] = blueprints.map((b) => b.path);

  const steps: DriveStep[] = blueprints.map((b) => ({
    element: () => firstVisibleMatch(b.selector) ?? document.body,
    popover: {
      title: t(b.titleKey),
      description: t(b.descKey),
      side: "bottom",
      align: "start",
    },
  }));

  const d = driver({
    showProgress: true,
    nextBtnText: t("common.next"),
    prevBtnText: t("common.previous"),
    doneBtnText: t("common.close"),
    steps,
    onNextClick: (_el, _step, { driver: drv }) => {
      const idx = drv.getActiveIndex() ?? 0;
      if (idx >= steps.length - 1) {
        drv.destroy();
        return;
      }
      const nextIdx = idx + 1;
      const required = pathsForStep[nextIdx];
      if (required != null && !pathMatches(required)) {
        navigate(required);
        const wait = delayForPath(required);
        window.setTimeout(() => {
          drv.refresh();
          drv.moveNext();
        }, wait);
        return;
      }
      drv.moveNext();
    },
    onPrevClick: (_el, _step, { driver: drv }) => {
      const idx = drv.getActiveIndex() ?? 0;
      if (idx <= 0) return;
      const prevIdx = idx - 1;
      const required = pathsForStep[prevIdx];
      if (required != null && !pathMatches(required)) {
        navigate(required);
        const wait = delayForPath(required);
        window.setTimeout(() => {
          drv.refresh();
          drv.movePrevious();
        }, wait);
        return;
      }
      drv.movePrevious();
    },
  });

  const firstPath = pathsForStep[0] ?? "/";
  if (!pathMatches(firstPath)) {
    navigate(firstPath);
    window.setTimeout(() => {
      d.refresh();
      d.drive(0);
    }, NAV_DELAY_MS);
    return;
  }

  d.drive(0);
}
